import PropTypes from 'prop-types';
import React from 'react';

import { SENDER_TYPES } from '../../utils/senderTypes';
import ChatMessageError from './ChatMessageError';
import ChatMessageItem from './ChatMessageItem';

/**
 * @typedef {Object} ChatMessage
 * @property {string} id
 * @property {'user'|'bot'} sender
 * @property {string} text
 * @property {string[]} [options]
 */

/**
 * @typedef {Object} ChatMessageListProps
 * @property {ChatMessage[]} messages
 * @property {string} [errorMessage]
 * @property {(value: string) => void} [onOptionSelect]
 * @property {function(): void} [onStartNewChat]
 */

/**
 * Chat message list (message thread).
 * @component
 * @param {ChatMessageListProps} props
 * @returns {JSX.Element}
 */
export default function ChatMessageList({
  activeMessageIndex = -1,
  messages,
  onActiveMessageIndexChange,
  onExitMessageNavigation,
  errorMessage,
  onOptionSelect,
  onStartNewChat,
  showAiDisclaimer = false,
}) {
  // With no message active, the last substantive(ignoring feedback prompts) message is the transcript's
  // tab stop so keyboard users can always reach the history from the page's
  // tab sequence.
  let defaultTabStopIndex = messages.length - 1;
  while (defaultTabStopIndex > 0 && messages[defaultTabStopIndex].isFeedback) {
    defaultTabStopIndex -= 1;
  }
  const tabStopIndex =
    activeMessageIndex >= 0 ? activeMessageIndex : defaultTabStopIndex;

  return (
    <ul
      aria-label="Conversation messages"
      aria-roledescription="chat transcript"
      className="vads-u-margin--0 vads-u-padding--1p5"
      data-testid="chat-message-list"
      style={{ listStyle: 'none' }}
    >
      {messages.map((message, index) => {
        const previousMessage = messages[index - 1];
        const showAvatar =
          message.sender !== SENDER_TYPES.BOT ||
          previousMessage?.sender !== SENDER_TYPES.BOT;

        return (
          <ChatMessageItem
            index={index}
            isActive={index === activeMessageIndex}
            isTabStop={index === tabStopIndex}
            key={message.id}
            message={message}
            onActivate={onActiveMessageIndexChange}
            onExitNavigation={onExitMessageNavigation}
            onMove={onActiveMessageIndexChange}
            onOptionSelect={onOptionSelect}
            showAiDisclaimer={showAiDisclaimer}
            showAvatar={showAvatar}
          />
        );
      })}
      {errorMessage ? (
        <ChatMessageError
          message={errorMessage}
          onStartNewChat={onStartNewChat}
        />
      ) : null}
    </ul>
  );
}

ChatMessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sender: PropTypes.oneOf([SENDER_TYPES.USER, SENDER_TYPES.BOT]).isRequired,
      text: PropTypes.string.isRequired,
      isFeedback: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  activeMessageIndex: PropTypes.number,
  errorMessage: PropTypes.string,
  showAiDisclaimer: PropTypes.bool,
  onActiveMessageIndexChange: PropTypes.func,
  onExitMessageNavigation: PropTypes.func,
  onOptionSelect: PropTypes.func,
  onStartNewChat: PropTypes.func,
};
